import type { IndicatorRegistryEntry } from "lightweight-charts-indicators"
import { EyeIcon, EyeOffIcon, GearIcon, TrashIcon } from "../../ui/icons.tsx"
import type { IndicatorInstance } from "../workspace/workspaceStore.ts"
import type { LegendValue } from "./IndicatorLayer.ts"

/** Short input summary, as in TradingView's legend: "RSI 14 close" (off with "Inputs in status line"). */
const inputSummary = (entry: IndicatorRegistryEntry, instance: IndicatorInstance) => {
  if (instance.style?.inputsInStatusLine === false) return ""
  const inputs = { ...(entry.defaultInputs as Record<string, unknown>), ...instance.inputs }
  return entry.inputConfig
    .filter((input) => input.display !== "none" && ["int", "float", "source"].includes(input.type))
    .map((input) => String(inputs[input.id]))
    .join(" ")
}

/** One row per indicator of a pane: name, inputs, current values, and hide / settings / remove. */
export function IndicatorLegend({ catalog, errors, instances, onOpenSettings, onRemove, onSelect, onToggle, selected, values }: {
  readonly instances: ReadonlyArray<IndicatorInstance>
  /** The indicator selected on the chart, highlighted here. */
  readonly selected: string | null
  readonly onSelect: (instance: IndicatorInstance) => void
  readonly catalog: ReadonlyMap<string, IndicatorRegistryEntry>
  readonly values: ReadonlyMap<string, ReadonlyArray<LegendValue>>
  readonly errors: ReadonlyMap<string, string>
  readonly onToggle: (instance: IndicatorInstance) => void
  readonly onOpenSettings: (instance: IndicatorInstance) => void
  readonly onRemove: (instance: IndicatorInstance) => void
}) {
  return (
    <ul className="indicator-legend">
      {instances.map((instance) => {
        const entry = catalog.get(instance.id)
        if (entry === undefined) return null
        const error = errors.get(instance.uid)
        return (
          <li
            key={instance.uid}
            className={[instance.hidden ? "legend-hidden" : "", selected === instance.uid ? "legend-selected" : ""].join(" ")}
            onClick={() => onSelect(instance)}
            onDoubleClick={() => onOpenSettings(instance)}
          >
            <span className="legend-name">{entry.shortName}</span>
            <span className="legend-inputs">{inputSummary(entry, instance)}</span>
            {error
              ? <span className="legend-error" title={error}>can't calculate</span>
              : (values.get(instance.uid) ?? []).map((value, i) => (
                <span key={i} className="legend-value" style={{ color: value.color }} title={value.title}>
                  {value.text}
                </span>
              ))}
            <span className="legend-actions" onClick={(event) => event.stopPropagation()}>
              <button type="button" title={instance.hidden ? "Show" : "Hide"} onClick={() => onToggle(instance)}>
                {instance.hidden ? <EyeOffIcon /> : <EyeIcon />}
              </button>
              <button type="button" title="Settings" onClick={() => onOpenSettings(instance)}>
                <GearIcon />
              </button>
              <button type="button" title="Remove" onClick={() => onRemove(instance)}>
                <TrashIcon />
              </button>
            </span>
          </li>
        )
      })}
    </ul>
  )
}
